import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, ElementRef, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatMenuModule } from '@angular/material/menu';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject, catchError, exhaustMap, filter, finalize, forkJoin, merge, of, timer, timeout } from 'rxjs';
import Chart from 'chart.js/auto';
import { AuthorizationService } from '../authorization/authorization.service';
import { ApplicationErrorLogResponse } from '../../shared/models/applicationErrorLogResponse';
import { ErrorType } from '../../shared/models/applicationErrorLogRequest';

// Shared dashboard record: normalized API fields and inferred classifications, without raw field values.
interface TelemetryLog {
  id: string;
  user: string;
  page: string;
  field: string;
  message: string;
  timestamp: number;
  type: ErrorType;
  status: string;
  category: string;
  service: string;
}

// View models for donut legends, page breakdowns, and heatmap rows.
interface Distribution { label: string; count: number; color: string; }
interface PageFailure { page: string; ui: number; api: number; total: number; share: number; severity: string; trigger: string; }
interface HeatRow { name: string; counts: number[]; }
interface RecurringIssue { message: string; page: string; field: string; type: ErrorType; count: number; lastSeen: number; users: string[]; }

@Component({
  selector: 'div.dtf-logtelemetry',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, MatIconModule, MatProgressSpinnerModule, MatMenuModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './log-telemetry.component.html',
  styleUrls: ['./log-telemetry.component.scss']
})
export class LogTelemetryComponent implements OnInit, AfterViewInit, OnDestroy {
  // Canvas references become available after Angular initializes the view.
  @ViewChild('validationCanvas') validationCanvas: ElementRef<HTMLCanvasElement>;
  @ViewChild('apiCanvas') apiCanvas: ElementRef<HTMLCanvasElement>;

  // Own polling subscriptions and chart instances so navigation releases their resources.
  private readonly destroyRef = inject(DestroyRef);
  private readonly changeDetector = inject(ChangeDetectorRef);
  private readonly refresh = new Subject<void>();
  private charts: Chart[] = [];
  private viewReady = false;

  // Preserve the latest complete snapshot separately from the globally filtered records.
  private allLogs: TelemetryLog[] = [];
  private scopedLogs: TelemetryLog[] = [];

  // Applied dates are separate from editable inputs; Sync View commits the requested range.
  private rangeStart = '';
  private rangeEnd = '';
  private rangeAllDates = true;
  // Include historical records on first load; custom dates apply only when All recorded dates is off.
  allDates = true;
  readonly today = this.dateInput(new Date());
  startDate = this.dateInput(new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate() - 6));
  endDate = this.today;
  activeType: 'ALL' | ErrorType = 'ALL';

  // Connection and snapshot state control loading, stale-data warnings, and refresh badges.
  live = true;
  loading = true;
  error = '';
  dateError = '';
  lastSynced: Date | null = null;
  invalidTimestamps = 0;
  outsideRange = 0;
  loadedCount = 0;

  // Summary values and distribution models are derived only from the selected log records.
  total = 0;
  validationCount = 0;
  apiCount = 0;
  impactedUsers = 0;
  unknownUsers = 0;
  validationDistribution: Distribution[] = [];
  apiDistribution: Distribution[] = [];
  services: { name: string; count: number; status: string }[] = [];
  pages: PageFailure[] = [];
  topIssues: RecurringIssue[] = [];

  // Heatmap columns represent six local-time, four-hour buckets across the selected dates.
  heatMode: 'page' | 'route' = 'page';
  readonly heatLabels = ['00:00 - 04:00', '04:00 - 08:00', '08:00 - 12:00', '12:00 - 16:00', '16:00 - 20:00', '20:00 - 24:00'];
  heatRows: HeatRow[] = [];
  heatMax = 0;
  heatTotalRows = 0;
  peakLabel = 'No events';

  // Table-only filters do not change the shared dashboard metrics or timeline.
  search = '';
  tableType: 'ALL' | ErrorType = 'ALL';
  tableLogs: TelemetryLog[] = [];
  visibleLogs: TelemetryLog[] = [];
  pageIndex = 0;
  readonly pageSize = 10;

  constructor(private readonly auth: AuthorizationService) {}

  // Fetch both endpoints initially, every 30 seconds when enabled, and on manual refresh.
  ngOnInit(): void {
    this.rangeStart = this.startDate;
    this.rangeEnd = this.endDate;
    merge(timer(0, 30000).pipe(filter(tick => tick === 0 || this.live)), this.refresh).pipe(
      // Ignore extra refresh triggers until the current pair of requests has completed.
      exhaustMap(() => {
        this.loading = true;
        // Timer-driven refreshes must explicitly schedule an OnPush view update.
        this.changeDetector.markForCheck();
        // Commit an atomic snapshot: one failed endpoint must not replace half the widgets.
        return forkJoin({
          validation: this.auth.getValidationErrors(),
          api: this.auth.getApiErrors()
        }).pipe(
          timeout(20000),
          catchError(() => {
            // Keep the previous snapshot usable while clearly identifying stale data.
            this.error = this.lastSynced
              ? 'Refresh failed. Showing the last successful snapshot; data is stale. Retry Sync View.'
              : 'Unable to load error logs. Check your connection and API access, then retry Sync View.';
            return of(null);
          }),
          // Finalize runs on success, errors, timeouts, and teardown; never update a destroyed view.
          finalize(() => {
            this.loading = false;
            if (!this.destroyRef.destroyed) {
              this.changeDetector.markForCheck();
            }
          })
        );
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(result => {
      if (!result) { return; }
      // The controller contract returns arrays directly, not wrapped response objects.
      if (!Array.isArray(result.validation) || !Array.isArray(result.api)) {
        this.error = 'Unexpected API response. Both error endpoints must return JSON arrays. Previous data has been retained.';
        return;
      }
      this.allLogs = [
        ...result.validation.map(row => this.normalize(row, 'VALIDATION')),
        ...result.api.map(row => this.normalize(row, 'API_ERROR'))
      ];
      this.loadedCount = this.allLogs.length;
      this.error = '';
      this.lastSynced = new Date();
      this.recalculate();
    });
  }

  // Chart.js needs actual canvas elements, so initial rendering waits for view initialization.
  ngAfterViewInit(): void {
    this.viewReady = true;
    this.drawCharts();
  }

  // Subscription cleanup is handled by takeUntilDestroyed; canvas resources need explicit cleanup.
  ngOnDestroy(): void {
    this.charts.forEach(chart => chart.destroy());
  }

  // Selecting a date opts into custom filtering; only Sync View commits it to the dashboard.
  onDateChange(boundary: 'start' | 'end', value: string): void {
    if (boundary === 'start') {
      this.startDate = value;
    } else {
      this.endDate = value;
    }
    this.allDates = false;
    this.dateError = '';
  }

  // Apply a valid inclusive date range to all widgets, then request a fresh backend snapshot.
  syncView(): void {
    if (!this.allDates && (!this.startDate || !this.endDate || this.startDate > this.endDate || this.endDate > this.today)) {
      this.dateError = 'Select a valid date range ending today or earlier.';
      return;
    }
    this.dateError = '';
    this.rangeStart = this.startDate;
    this.rangeEnd = this.endDate;
    this.rangeAllDates = this.allDates;
    this.recalculate();
    this.refresh.next();
  }

  // Global tabs narrow every chart, summary, page breakdown, and the recent-log source together.
  setType(type: 'ALL' | ErrorType): void {
    this.activeType = type;
    this.recalculate();
  }

  // Empty datasets produce zero percentages rather than NaN or Infinity.
  percent(count: number, total = this.total): number {
    return total ? count / total * 100 : 0;
  }

  // Keep the empty table on page one and show the range that was actually applied.
  get pageCount(): number { return Math.max(1, Math.ceil(this.tableLogs.length / this.pageSize)); }
  get appliedRange(): string { return this.rangeAllDates ? 'All recorded dates' : `${this.rangeStart} to ${this.rangeEnd}`; }

  // Format native date-input values using local calendar dates, avoiding UTC date shifts.
  private dateInput(date: Date): string {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  }

  // Jackson uses one-based months and nanoseconds; JavaScript uses zero-based months and milliseconds.
  private parseTimestamp(value: unknown): number {
    if (Array.isArray(value)) {
      if (value.length < 3 || value.length > 7 || !value.every(part => typeof part === 'number' && Number.isInteger(part))) {
        return NaN;
      }
      const [year, month, day, hour = 0, minute = 0, second = 0, nanosecond = 0] = value;
      if (month < 1 || month > 12 || day < 1 || day > 31 || hour < 0 || hour > 23
        || minute < 0 || minute > 59 || second < 0 || second > 59 || nanosecond < 0 || nanosecond > 999999999) {
        return NaN;
      }
      const date = new Date(0);
      date.setFullYear(year, month - 1, day);
      date.setHours(hour, minute, second, Math.floor(nanosecond / 1000000));
      return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day ? date.getTime() : NaN;
    }
    return typeof value === 'string' && value.trim() ? Date.parse(value) : NaN;
  }

  // Accept DTO camelCase and database-style snake_case keys in one consistent record shape.
  private normalize(row: ApplicationErrorLogResponse, type: ErrorType): TelemetryLog {
    const source = row as unknown as Record<string, unknown>;
    const value = (camel: string, snake: string): string => String(source[camel] ?? source[snake] ?? source[snake.toLowerCase()] ?? '');
    const rawMessage = value('errorMessage', 'ERROR_MESSAGE');
    const field = value('fieldName', 'FIELD_NAME');
    const page = value('pageName', 'PAGE_NAME') || 'Unknown page';
    const rawUser = value('userId', 'USER_ID').trim();
    const rawValue = value('fieldValue', 'FIELD_VALUE');
    // Remove recorded field values and recognizable sensitive data before display and export.
    let message = rawValue ? rawMessage.split(rawValue).join('[REDACTED]') : rawMessage;
    message = message.replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, '[EMAIL REDACTED]')
      .replace(/\b\d{3}[- ]?\d{2}[- ]?\d{4}\b/g, '[SSN REDACTED]')
      .replace(/((?:password|token|secret|authorization)\s*[=:]\s*)[^\s,;]+/gi, '$1[REDACTED]');
    const categoryText = `${field} ${rawMessage}`.toLowerCase();
    const status = type === 'API_ERROR' ? this.httpStatus(rawMessage) : '';
    const routeText = `${page} ${field}`.toLowerCase();
    // Categories and services are inferred labels, not confirmed backend health or schema metadata.
    return {
      id: value('id', 'ID') || 'Not recorded',
      user: /^(undefined|null|unknown|anonymous)$/i.test(rawUser) ? '' : rawUser, page, field: field || 'Not recorded',
      message: message || 'No error message recorded',
      timestamp: this.parseTimestamp(source['errorTimestamp'] ?? source['ERROR_TIMESTAMP'] ?? source['error_timestamp']),
      type, status,
      // Keep API errors without HTTP metadata grouped by their actual sanitized message.
      category: type === 'API_ERROR' ? (status === 'UNKNOWN' ? message || 'No error message recorded' : this.apiCategory(status)) :
        /email/.test(categoryText) ? 'Invalid Email Format' :
        /password|passwd/.test(categoryText) ? 'Password Complexity Failure' :
        /ssn|social.?security/.test(categoryText) ? 'Invalid SSN Format' :
        /required|mandatory|missing|must not be (blank|null|empty)/.test(categoryText) ? 'Required Field Missing' :
        /schema|constraint|pattern/.test(categoryText) ? 'Custom Schema Validation' : 'Other Validation Errors',
      service: /auth|login|mfa|password|token|verifyuser|userid/.test(routeText) ? 'Authentication' :
        /pay|billing|checkout|invoice/.test(routeText) ? 'Payment Gateway' :
        /customer|profile|register|registration|kyc/.test(routeText) ? 'Customer API' :
        /portal|dashboard|document/.test(routeText) ? 'Portal API' : 'Unclassified'
    };
  }

  // Prefer an explicit HTTP status; recognizable messages are a fallback, otherwise retain UNKNOWN.
  // The response DTO statusCode can be a business response code and is not treated as HTTP status.
  private httpStatus(message: string): string {
    const explicit = message.match(/\b(?:HTTP(?:\s+status)?|status(?:\s*code)?)\s*[:=]?\s*([45]\d{2})\b/i)
      || message.match(/\(([45]\d{2})\)/)
      || message.match(/^\s*([45]\d{2})\b/);
    if (explicit) { return explicit[1]; }
    const names: [RegExp, string][] = [
      [/gateway\s*time.?out/i, '504'], [/bad\s*gateway/i, '502'], [/service\s*unavailable/i, '503'],
      [/internal\s*server\s*error/i, '500'], [/unauthori[sz]ed/i, '401'], [/forbidden/i, '403'],
      [/not\s*found/i, '404'], [/bad\s*request/i, '400']
    ];
    return names.find(([pattern]) => pattern.test(message))?.[1] || (/time.?out|timed out/i.test(message) ? 'TIMEOUT' : 'UNKNOWN');
  }

  // Use readable, mutually exclusive API legend labels so each error contributes only once.
  private apiCategory(status: string): string {
    return ({ '400': '400 / Bad Request', '401': '401 / Unauthorized', '403': '403 / Forbidden',
      '404': '404 / Not Found', '500': '500 / Internal Server Error', '502': '502 / Bad Gateway',
      '503': '503 / Service Unavailable', '504': '504 / Gateway Timeout', TIMEOUT: 'Timeout Failure',
      UNKNOWN: 'Unclassified API Error' } as Record<string, string>)[status] || `${status} / Other HTTP Error`;
  }

  // Rebuild every dashboard view from the same date- and type-filtered snapshot.
  private recalculate(): void {
    const start = new Date(`${this.rangeStart}T00:00:00`).getTime();
    const end = new Date(`${this.rangeEnd}T00:00:00`);
    // An exclusive next-day boundary includes the entire selected end date, including milliseconds.
    end.setDate(end.getDate() + 1);
    this.invalidTimestamps = this.allLogs.filter(log => !Number.isFinite(log.timestamp)).length;
    const datedLogs = this.allLogs.filter(log => Number.isFinite(log.timestamp)
      && (this.rangeAllDates || (log.timestamp >= start && log.timestamp < end.getTime())));
    this.outsideRange = this.allLogs.length - this.invalidTimestamps - datedLogs.length;
    this.scopedLogs = datedLogs.filter(log => this.activeType === 'ALL' || log.type === this.activeType)
      .sort((first, second) => second.timestamp - first.timestamp);
    const validation = this.scopedLogs.filter(log => log.type === 'VALIDATION');
    const api = this.scopedLogs.filter(log => log.type === 'API_ERROR');
    this.total = this.scopedLogs.length;
    this.validationCount = validation.length;
    this.apiCount = api.length;
    // USER_ID supports distinct impacted users, but cannot establish session counts or success rates.
    this.impactedUsers = new Set(this.scopedLogs.map(log => log.user).filter(user => user.trim())).size;
    this.unknownUsers = this.scopedLogs.filter(log => !log.user.trim()).length;
    this.validationDistribution = this.distribution(validation, ['#2864f6', '#5695ff', '#86b9ff', '#b9d8ff', '#f2a000']);
    this.apiDistribution = this.distribution(api, ['#f43f64', '#ff7621', '#edb900', '#d34ca5', '#8d70d8', '#38a5ae', '#6c849f', '#df5775', '#b27c28', '#94a3b8']);
    // No observed errors means Unknown, not Healthy; true health requires separate health checks.
    this.services = ['Authentication', 'Customer API', 'Payment Gateway', 'Portal API', 'Unclassified'].map(name => {
      const matching = api.filter(log => log.service === name);
      return { name, count: matching.length, status: matching.some(log => /^5|TIMEOUT/.test(log.status)) ? 'Critical' : matching.length ? 'Degraded' : 'Unknown' };
    });
    // Group in one pass before ranking pages by total failures and their most frequent message.
    const groups = new Map<string, TelemetryLog[]>();
    this.scopedLogs.forEach(log => {
      const group = groups.get(log.page) || [];
      group.push(log);
      groups.set(log.page, group);
    });
    this.pages = Array.from(groups, ([page, logs]) => {
      const ui = logs.filter(log => log.type === 'VALIDATION').length;
      const messages = new Map<string, number>();
      logs.forEach(log => messages.set(log.message, (messages.get(log.message) || 0) + 1));
      return { page, ui, api: logs.length - ui, total: logs.length, share: this.percent(logs.length),
        severity: logs.some(log => /^5|TIMEOUT/.test(log.status)) ? 'Critical' : logs.length - ui ? 'High friction' : 'Validation friction',
        trigger: Array.from(messages).sort((first, second) => second[1] - first[1])[0][0] };
    }).sort((first, second) => second.total - first.total);
    this.buildTopIssues();
    this.buildHeatmap();
    this.updateTable();
    this.drawCharts();
    // Notify Angular after async data aggregation, including charts, KPIs, and table rows.
    this.changeDetector.markForCheck();
  }

  // Show only observed categories; unknown API statuses use actual backend messages, not example labels.
  private distribution(logs: TelemetryLog[], colors: string[]): Distribution[] {
    const counts = new Map<string, number>();
    logs.forEach(log => counts.set(log.category, (counts.get(log.category) || 0) + 1));
    return Array.from(counts).sort((first, second) => second[1] - first[1] || first[0].localeCompare(second[0]))
      .map(([label, count], index) => ({ label, count, color: colors[index % colors.length] }));
  }

  // Rank sanitized messages within their page, field, and type; do not merge unrelated failures.
  private buildTopIssues(): void {
    const issues = new Map<string, RecurringIssue>();
    this.scopedLogs.forEach(log => {
      const message = log.message.trim().replace(/\s+/g, ' ');
      const key = JSON.stringify([log.type, log.page, log.field, message.toLowerCase()]);
      const issue = issues.get(key);
      if (issue) {
        issue.count++;
        issue.lastSeen = Math.max(issue.lastSeen, log.timestamp);
        if (log.user && !issue.users.includes(log.user)) issue.users.push(log.user);
      } else {
        issues.set(key, { message, page: log.page, field: log.field, type: log.type, count: 1, lastSeen: log.timestamp, users: log.user ? [log.user] : [] });
      }
    });
    this.topIssues = Array.from(issues.values()).sort((first, second) => second.count - first.count
      || second.lastSeen - first.lastSeen || first.message.localeCompare(second.message)).slice(0, 5);
  }

  // Aggregate pages or API field/endpoint names into local four-hour time-of-day buckets.
  buildHeatmap(): void {
    const groups = new Map<string, number[]>();
    this.scopedLogs.filter(log => this.heatMode === 'page' || log.type === 'API_ERROR').forEach(log => {
      const name = this.heatMode === 'page' ? log.page : log.field;
      const counts = groups.get(name) || Array(6).fill(0);
      counts[Math.floor(new Date(log.timestamp).getHours() / 4)]++;
      groups.set(name, counts);
    });
    this.heatTotalRows = groups.size;
    // Display the busiest twelve groups while calculating the peak window across all groups.
    this.heatRows = Array.from(groups, ([name, counts]) => ({ name, counts }))
      .sort((first, second) => second.counts.reduce((sum, count) => sum + count, 0) - first.counts.reduce((sum, count) => sum + count, 0)).slice(0, 12);
    this.heatMax = Math.max(0, ...this.heatRows.flatMap(row => row.counts));
    const totals = Array(6).fill(0);
    groups.forEach(counts => counts.forEach((count, index) => totals[index] += count));
    const peak = Math.max(...totals);
    this.peakLabel = peak ? `${this.heatLabels[totals.indexOf(peak)]} (${peak.toLocaleString()} events)` : 'No events';
  }

  // Heatmap intensity is relative to the largest visible cell, not a fixed incident threshold.
  heatLevel(count: number): string {
    if (!count) { return 'empty'; }
    const ratio = count / this.heatMax;
    return ratio > .75 ? 'surge' : ratio > .5 ? 'elevated' : ratio > .25 ? 'moderate' : 'low';
  }

  // Search sanitized records and reset pagination when the table selection changes.
  updateTable(): void {
    const query = this.search.trim().toLowerCase();
    this.tableLogs = this.scopedLogs.filter(log => (this.tableType === 'ALL' || log.type === this.tableType)
      && (!query || [log.id, log.user, log.page, log.field, log.message, log.status].some(value => value.toLowerCase().includes(query))));
    this.pageIndex = 0;
    this.updateVisibleLogs();
  }

  // Clamp pagination to valid bounds, including the empty-table case.
  changePage(delta: number): void {
    this.pageIndex = Math.min(this.pageCount - 1, Math.max(0, this.pageIndex + delta));
    this.updateVisibleLogs();
  }

  // Render only the current page while keeping all matching rows available for export.
  private updateVisibleLogs(): void {
    this.visibleLogs = this.tableLogs.slice(this.pageIndex * this.pageSize, (this.pageIndex + 1) * this.pageSize);
  }

  // Export all matching records, not only the visible page, without exposing raw field values.
  exportLogs(): void {
    const payload = this.tableLogs.map(log => ({ id: log.id, userId: log.user, pageName: log.page,
      fieldName: log.field, fieldValue: '[REDACTED]', errorMessage: log.message,
      errorType: log.type, errorTimestamp: new Date(log.timestamp).toISOString(), inferredHttpStatus: log.status }));
    const url = URL.createObjectURL(new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' }));
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = this.rangeAllDates ? 'dtf-errors-all-dates.json' : `dtf-errors-${this.rangeStart}-${this.rangeEnd}.json`;
    anchor.click();
    URL.revokeObjectURL(url);
  }

  // Replace existing chart instances after recalculation to avoid duplicate canvas ownership.
  private drawCharts(): void {
    if (!this.viewReady) { return; }
    this.charts.forEach(chart => chart.destroy());
    this.charts = [
      { canvas: this.validationCanvas, data: this.validationDistribution },
      { canvas: this.apiCanvas, data: this.apiDistribution }
    ].map(({ canvas, data }) => new Chart(canvas.nativeElement, {
      type: 'doughnut',
      data: { labels: data.map(item => item.label), datasets: [{ data: data.map(item => item.count), backgroundColor: data.map(item => item.color), borderWidth: 0, hoverOffset: 3 }] },
      options: { responsive: true, maintainAspectRatio: false, cutout: '72%', animation: false,
        plugins: { legend: { display: false }, tooltip: { enabled: true } } }
    }));
  }
}